import {
    LEARNING_TO_CODE_COURSES,
    PROGRAMMER_RESUMES_COURSES,
    TECH_CONCEPTS_COURSES,
    TECH_INTERVIEW_PREP_COURSES
} from "../constants/CoursesConfig";
import {GenericContent} from "../models/GenericContent";
import {ALL_INTERVIEWS} from "../constants/InterviewsConfig";
import {ALL_RESOURCES} from "../constants/ResourcesConfig";
import {TOOL_TYPES_CONFIG} from "../constants/ToolTypesConfig";

export type JourneyCategory =
    | 'interested_in_code'
    | 'learning_to_code'
    | 'understanding_tech_concepts'
    | 'job_searching'
    | 'interviewing';

export interface JourneyStep {
    id: JourneyCategory;
    label: string;
    isCompleted: boolean;
    isCurrent: boolean;
}

export function categorizeUserJourney(answers: Record<string, string | string[]>): JourneyCategory {
    const devStage = answers.dev_stage as string;
    const devKnowledge = answers.dev_knowledge as string;
    const skillGrowth = answers.skill_growth as string;
    
    switch (devStage) {
        case 'interested':
            return 'interested_in_code';
        case 'learning':
            return 'learning_to_code';
        case 'job_searching':
            switch (skillGrowth) {
                case 'resume':
                    return 'job_searching';
                case 'interviews':
                    return 'job_searching';
            }
            return 'job_searching';
        case 'already_working':
            return 'understanding_tech_concepts';
        default:
            return 'interested_in_code';
    }
}

export function userJourneyPreviews(answers: Record<string, string | string[]>): GenericContent[] {
    const devStage = answers.dev_stage as string;
    const devKnowledge = answers.dev_knowledge as string;
    const skillGrowth = answers.skill_growth as string;
    const previewsToRetun: GenericContent[] = [];

    switch (devStage) {
        case 'interested':
            previewsToRetun.push(LEARNING_TO_CODE_COURSES[0]) // Want to Become a Programmer? Start Here
            previewsToRetun.push(ALL_INTERVIEWS.FORMAL_INTERVIEWS[0]) // From Elementary School Teacher to Software Engineer
            previewsToRetun.push(LEARNING_TO_CODE_COURSES[1]) // Programming: Choosing and Learning Your First Language
            previewsToRetun.push(ALL_RESOURCES.GUIDES[1]) // Basic Programming Data Structures
            previewsToRetun.push(LEARNING_TO_CODE_COURSES[2]) // Debugging 101
            break;
        case 'learning':
            previewsToRetun.push(LEARNING_TO_CODE_COURSES[1]) // Programming: Choosing and Learning Your First Language
            previewsToRetun.push(ALL_INTERVIEWS.FORMAL_INTERVIEWS[0]) // From Elementary School Teacher to Software Engineer
            previewsToRetun.push(LEARNING_TO_CODE_COURSES[2]) // Debugging 101
            previewsToRetun.push(ALL_RESOURCES.GUIDES[0]) // Github Reference Guide
            previewsToRetun.push(TECH_CONCEPTS_COURSES[0]) // What Are APIs
            previewsToRetun.push(TECH_CONCEPTS_COURSES[1]) // A Dev's Guide to Project Management
            break;
        case 'job_searching':
            previewsToRetun.push(PROGRAMMER_RESUMES_COURSES[1]) // How to Write Your Tech Resume
            previewsToRetun.push(ALL_INTERVIEWS.MOCK_INTERVIEWS[0]) // Mock Interview (Non Technical): Entry Level Position
            previewsToRetun.push(PROGRAMMER_RESUMES_COURSES[0]) // Building a Tech Portfolio
            previewsToRetun.push(ALL_RESOURCES.GUIDES[5]) // The Ultimate Coding Interview Study Guide
            previewsToRetun.push(TOOL_TYPES_CONFIG[0]) // Resume Reviewer
            previewsToRetun.push(PROGRAMMER_RESUMES_COURSES[2]) // How to Write a Tech Cover Letter
            previewsToRetun.push(TECH_INTERVIEW_PREP_COURSES[0]) // Interview Prep (Technical)
            previewsToRetun.push(TECH_INTERVIEW_PREP_COURSES[1]) // Interview Prep (Non-Technical)
            previewsToRetun.push(PROGRAMMER_RESUMES_COURSES[0]) // Building a Tech Portfolio
            break;
        case 'already_working':
            previewsToRetun.push(LEARNING_TO_CODE_COURSES[2]) // Debugging 101
            previewsToRetun.push(TECH_CONCEPTS_COURSES[0]) // What Are APIs
            previewsToRetun.push(ALL_RESOURCES.GUIDES[4]) // Advanced Data Structures
            previewsToRetun.push(TECH_CONCEPTS_COURSES[1]) // A Dev's Guide to Project Management
            previewsToRetun.push(ALL_INTERVIEWS.FORMAL_INTERVIEWS[2]) // Finding Meaningful Work – What Makes a Successful Engineer
            previewsToRetun.push(TECH_INTERVIEW_PREP_COURSES[0]) // Interview Prep (Technical)
            previewsToRetun.push(TECH_INTERVIEW_PREP_COURSES[1]) // Interview Prep (Non-Technical)
            break;
    }
    return previewsToRetun;
}

export function getJourneySteps(currentCategory: JourneyCategory): JourneyStep[] {
    const allSteps: JourneyStep[] = [
        {
            id: 'interested_in_code',
            label: 'Interested in Code',
            isCompleted: false,
            isCurrent: false
        },
        {
            id: 'learning_to_code',
            label: 'Learning to Code',
            isCompleted: false,
            isCurrent: false
        },
        {
            id: 'understanding_tech_concepts',
            label: 'Understanding Tech Concepts',
            isCompleted: false,
            isCurrent: false
        },
        {
            id: 'job_searching',
            label: 'Job Searching',
            isCompleted: false,
            isCurrent: false
        },
        {
            id: 'interviewing',
            label: 'Interviewing',
            isCompleted: false,
            isCurrent: false
        }
    ];
    
    // Mark steps as completed or current based on the user's journey
    const categoryIndex = allSteps.findIndex(step => step.id === currentCategory);
    
    allSteps.forEach((step, index) => {
        if (index < categoryIndex) {
            step.isCompleted = true;
        } else if (index === categoryIndex) {
            step.isCurrent = true;
        }
    });
    
    return allSteps;
}